import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import Button from '../components/ui/Button';
import AuthLayout from '../components/AuthLayout';
import { useRegister } from '../hooks/useAuth';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';
import { normalizeFieldErrors } from '../services/api';

const Register = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1 = invite code, 2 = form
    const [inviteCode, setInviteCode] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const register = useRegister();

    const handleInviteCodeChange = (e) => {
        setInviteCode(e.target.value);
        if (error) setError('');
    };

    const handleContinueToForm = (e) => {
        e.preventDefault();
        if (!inviteCode.trim()) {
            setError(t(KEYS.REGISTER_ERROR_INVITE_REQUIRED));
            return;
        }
        setError('');
        setStep(2);
    };

    const handleBackToCode = () => {
        setError('');
        setStep(1);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({ ...prev, [name]: '' }));
        }
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});
        setLoading(true);

        if (formData.password !== formData.confirmPassword) {
            setError(t(KEYS.REGISTER_ERROR_PASSWORDS_MISMATCH));
            setLoading(false);
            return;
        }

        try {
            await register.mutateAsync({
                name: formData.name,
                email: formData.email,
                password: formData.password,
                passwordConfirmation: formData.confirmPassword,
                inviteCode: inviteCode,
            });

            // Auto-logged in — redirect to dashboard
            navigate('/');

        } catch (err) {
            if (err.code === 'VALIDATION_ERROR' && err.errors) {
                const normalized = normalizeFieldErrors(err.errors);
                // invite_code errors — show on step 1
                if (normalized.invite_code) {
                    setError(normalized.invite_code);
                    setStep(1);
                } else {
                    setFieldErrors(normalized);
                }
            } else {
                setError(err.message || t(KEYS.REGISTER_ERROR_GENERIC));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout subtitle={step === 1 ? t(KEYS.REGISTER_TAGLINE_INVITE) : t(KEYS.REGISTER_TAGLINE_FORM)}>
            {/* Step 1: Invite Code */}
                    {step === 1 && (
                        <form onSubmit={handleContinueToForm} className="space-y-4">
                            <div className="space-y-1.5">
                                <label htmlFor="inviteCode" className="label-base">
                                    {t(KEYS.REGISTER_INVITE_CODE)}
                                </label>
                                <input
                                    type="text"
                                    id="inviteCode"
                                    value={inviteCode}
                                    onChange={handleInviteCodeChange}
                                    required
                                    className="input-base"
                                    placeholder={t(KEYS.REGISTER_INVITE_PLACEHOLDER)}
                                    disabled={loading}
                                />
                            </div>

                            {/* Error */}
                            {error && (
                                <div className="p-3 bg-status-overdue-bg text-status-overdue rounded-md text-sm border border-status-overdue/20 animate-appear">
                                    {error}
                                </div>
                            )}

                            {/* Submit */}
                            <Button 
                                type="submit" 
                                variant="primary"
                                fullWidth
                                disabled={loading}
                            >
                                {loading ? t(KEYS.REGISTER_CONTINUING) : t(KEYS.REGISTER_CONTINUE)}
                            </Button>
                        </form>
                    )}

                    {/* Step 2: Registration Form */}
                    {step === 2 && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Name */}
                        <div className="space-y-1.5">
                            <label htmlFor="name" className="label-base">
                                {t(KEYS.COMMON_NAME)}
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className={`input-base ${fieldErrors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
                                placeholder="Jane Doe"
                                disabled={loading}
                            />
                            {fieldErrors.name && <span className="text-xs text-red-500 mt-1">{fieldErrors.name}</span>}
                        </div>

                        {/* Email */}
                        <div className="space-y-1.5">
                            <label htmlFor="email" className="label-base">
                                {t(KEYS.COMMON_EMAIL)}
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className={`input-base ${fieldErrors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                                placeholder="you@example.com"
                                disabled={loading}
                            />
                            {fieldErrors.email && <span className="text-xs text-red-500 mt-1">{fieldErrors.email}</span>}
                        </div>

                        {/* Password */}
                        <div className="space-y-1.5">
                            <label htmlFor="password" className="label-base">
                                {t(KEYS.COMMON_PASSWORD)}
                            </label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                                minLength="6"
                                className={`input-base ${fieldErrors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                                placeholder="••••••••"
                                disabled={loading}
                            />
                            {fieldErrors.password && <span className="text-xs text-red-500 mt-1">{fieldErrors.password}</span>}
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-1.5">
                            <label htmlFor="confirmPassword" className="label-base">
                                {t(KEYS.REGISTER_CONFIRM_PASSWORD)}
                            </label>
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                required
                                minLength="6"
                                className="input-base"
                                placeholder="••••••••"
                                disabled={loading}
                            />
                        </div>

                        {/* Error */}
                        {error && (
                            <div className="p-3 bg-status-overdue-bg text-status-overdue rounded-md text-sm border border-status-overdue/20 animate-appear">
                                {error}
                            </div>
                        )}

                            {/* Submit */}
                            <Button 
                                type="submit" 
                                variant="primary"
                                fullWidth
                                disabled={loading}
                            >
                                {loading ? t(KEYS.REGISTER_SUBMITTING) : t(KEYS.REGISTER_SUBMIT)}
                            </Button>

                            {/* Back Button */}
                            <Button 
                                type="button"
                                variant="secondary"
                                fullWidth
                                disabled={loading}
                                onClick={handleBackToCode}
                            >
                                {t(KEYS.COMMON_BACK)}
                            </Button>
                        </form>
                    )}

                    {/* Login Link - always shown */}
                    <div className="mt-5 pt-5 border-t border-surface-border text-center">
                        <p className="text-sm text-text-muted">
                            {t(KEYS.REGISTER_ALREADY_ACCOUNT)}{' '}
                            <Link to="/login" className="text-accent hover:text-accent-hover font-medium">
                                {t(KEYS.REGISTER_SIGN_IN)}
                            </Link>
                        </p>
                    </div>
        </AuthLayout>
    );
};

export default Register;
